import React, { useEffect, useState } from 'react';
import { CalendarIcon, MapPinIcon, TagIcon, SaveIcon, XIcon, ImageIcon, TrashIcon, EditIcon, ClipboardCheckIcon, ClockIcon, DollarSignIcon, AlertCircleIcon, LinkIcon, SparklesIcon, LoaderIcon } from 'lucide-react';
export function EventForm({
  event = null,
  ocrData = null,
  isEditing = false,
  onSubmit,
  onCancel
}) {
  const [eventData, setEventData] = useState({
    name: '',
    date: '',
    time: '',
    endTime: '',
    location: '',
    category: '',
    notes: '',
    status: 'interested',
    image: null,
    requiresRegistration: false,
    registrationDeadline: '',
    isFree: true,
    feeAmount: '',
    sourceType: 'photo',
    link: ''
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [showOcrNotice, setShowOcrNotice] = useState(false);
  const [isExtractingUrl, setIsExtractingUrl] = useState(false);
  const [extractionError, setExtractionError] = useState(null);
  // If editing, populate the form with event data
  useEffect(() => {
    if (event) {
      setEventData(event);
      if (event.image) {
        setImagePreview(event.image);
      }
    } else if (ocrData) {
      setEventData({
        ...eventData,
        ...ocrData,
        sourceType: 'photo'
      });
      if (ocrData.image) {
        setImagePreview(ocrData.image);
      }
      setShowOcrNotice(true);
    }
  }, [event, ocrData]);
  const handleChange = e => {
    const {
      name,
      value,
      type,
      checked
    } = e.target;
    setEventData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  const handleFeeOptionChange = isFree => {
    setEventData(prev => ({
      ...prev,
      isFree,
      // Clear fee amount if switching to free
      feeAmount: isFree ? '' : prev.feeAmount
    }));
  };
  const handleImageUpload = e => {
    const file = e.target.files[0];
    if (!file) return;
    // Check if file is an image
    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file');
      return;
    }
    // Check file size (limit to 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size should be less than 5MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = event => {
      setImagePreview(event.target.result);
      setEventData(prev => ({
        ...prev,
        image: event.target.result // Store base64 encoded image
      }));
    };
    reader.readAsDataURL(file);
  };
  const removeImage = () => {
    setImagePreview(null);
    setEventData(prev => ({
      ...prev,
      image: null
    }));
  };
  const extractMetadataFromUrl = async () => {
    if (!eventData.link || !eventData.link.trim()) {
      setExtractionError('Please enter a URL first');
      return;
    }
    setIsExtractingUrl(true);
    setExtractionError(null);
    try {
      // Attempt to fetch the URL
      const response = await fetch(eventData.link, {
        mode: 'cors'
      });
      if (!response.ok) {
        throw new Error('Failed to fetch URL');
      }
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      // Extract Open Graph and meta tags
      const getMetaContent = property => {
        const ogTag = doc.querySelector(`meta[property="${property}"]`);
        const nameTag = doc.querySelector(`meta[name="${property}"]`);
        return ogTag?.content || nameTag?.content || '';
      };
      // Extract event details
      const title = getMetaContent('og:title') || doc.querySelector('title')?.textContent || '';
      const description = getMetaContent('og:description') || getMetaContent('description') || '';
      const imageUrl = getMetaContent('og:image') || '';
      const eventDate = getMetaContent('event:start_date') || '';
      const location = getMetaContent('event:location') || '';
      // Update form with extracted data
      const updates = {};
      if (title && !eventData.name) {
        updates.name = title.trim();
      }
      if (description && !eventData.notes) {
        updates.notes = description.trim();
      }
      if (imageUrl && !eventData.image) {
        updates.image = imageUrl;
        setImagePreview(imageUrl);
      }
      if (location && !eventData.location) {
        updates.location = location.trim();
      }
      // Parse date if available
      if (eventDate && !eventData.date) {
        try {
          const parsedDate = new Date(eventDate);
          if (!isNaN(parsedDate.getTime())) {
            updates.date = parsedDate.toISOString().split('T')[0];
            // Extract time if present
            const timeStr = parsedDate.toTimeString().slice(0, 5);
            if (timeStr !== '00:00') {
              updates.time = timeStr;
            }
          }
        } catch (e) {
          console.error('Error parsing date:', e);
        }
      }
      if (Object.keys(updates).length > 0) {
        setEventData(prev => ({
          ...prev,
          ...updates
        }));
        setShowOcrNotice(true);
      } else {
        setExtractionError('No event details found. Please fill in manually.');
      }
    } catch (error) {
      console.error('Error extracting metadata:', error);
      if (error.message.includes('CORS') || error.name === 'TypeError') {
        setExtractionError('Unable to access this URL due to security restrictions. For Instagram, Facebook, or Eventbrite links, please copy the event details manually.');
      } else {
        setExtractionError('Could not extract event details from this URL. Please fill in the information manually.');
      }
    } finally {
      setIsExtractingUrl(false);
    }
  };
  const handleSubmit = e => {
    e.preventDefault();
    if (isEditing) {
      // Keep the original id and dateCreated when updating
      onSubmit(eventData);
    } else {
      // Add dateCreated for new events
      onSubmit({
        ...eventData,
        dateCreated: new Date().toISOString()
      });
      // Reset form after submission (only for new events)
      setEventData({
        name: '',
        date: '',
        time: '',
        endTime: '',
        location: '',
        category: '',
        notes: '',
        status: 'interested',
        image: null,
        requiresRegistration: false,
        registrationDeadline: '',
        isFree: true,
        feeAmount: '',
        sourceType: '',
        link: ''
      });
      setImagePreview(null);
      setShowOcrNotice(false);
    }
  };
  return <div className="bg-white rounded-lg shadow p-6" data-id="element-128">
      <div className="flex justify-between items-center mb-4" data-id="element-129">
        <h2 className="text-lg font-medium" data-id="element-130">
          {isEditing ? <span className="flex items-center" data-id="element-131">
              <EditIcon className="w-5 h-5 mr-2" data-id="element-132" />
              Edit Event
            </span> : 'Save a new event'}
        </h2>
        {onCancel && <button onClick={onCancel} className="text-gray-500 hover:text-gray-700" data-id="element-133">
            <XIcon className="w-5 h-5" data-id="element-134" />
          </button>}
      </div>
      {/* OCR Notice */}
      {showOcrNotice && <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md" data-id="element-135">
          <div className="flex items-start" data-id="element-136">
            <AlertCircleIcon className="w-5 h-5 text-blue-500 mr-2 mt-0.5" data-id="element-137" />
            <div data-id="element-138">
              <p className="text-sm font-medium text-blue-800" data-id="element-139">
                {ocrData ? 'Detected from image' : 'Extracted from URL'}
              </p>
              <p className="text-xs text-blue-600 mt-1" data-id="element-140">
                {ocrData ? "We've extracted event details from your image. Please review and correct any information if needed." : "We've extracted available event details from the URL. Please review and add any missing information."}
              </p>
            </div>
          </div>
        </div>}
      <form onSubmit={handleSubmit} data-id="element-141">
        <div className="space-y-4" data-id="element-142">
          {/* Event Name */}
          <div data-id="element-143">
            <label htmlFor="name" className="block text-sm font-medium text-gray-700" data-id="element-144">
              Event Name*
            </label>
            <input type="text" id="name" name="name" required value={eventData.name} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" placeholder="Concert, Festival, Workshop..." data-id="element-145" />
          </div>
          {/* Image Upload */}
          <div data-id="element-146">
            <label htmlFor="image" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-147">
              <ImageIcon className="inline-block w-4 h-4 mr-1" data-id="element-148" /> Event Image
            </label>
            {imagePreview ? <div className="mt-2 relative" data-id="element-149">
                <img src={imagePreview} alt="Event preview" className="w-full h-48 object-cover rounded-md" data-id="element-150" />
                <button type="button" onClick={removeImage} className="absolute top-2 right-2 bg-white rounded-full p-1 shadow-sm hover:bg-gray-100" data-id="element-151">
                  <TrashIcon className="w-5 h-5 text-red-500" data-id="element-152" />
                </button>
              </div> : <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md" data-id="element-153">
                <div className="space-y-1 text-center" data-id="element-154">
                  <ImageIcon className="mx-auto h-12 w-12 text-gray-400" data-id="element-155" />
                  <div className="flex text-sm text-gray-600" data-id="element-156">
                    <label htmlFor="image-upload" className="relative cursor-pointer rounded-md bg-white font-medium text-blue-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-offset-2 hover:text-blue-500" data-id="element-157">
                      <span data-id="element-158">Upload a screenshot</span>
                      <input id="image-upload" name="image-upload" type="file" accept="image/*" className="sr-only" onChange={handleImageUpload} data-id="element-159" />
                    </label>
                    <p className="pl-1" data-id="element-160">or drag and drop</p>
                  </div>
                  <p className="text-xs text-gray-500" data-id="element-161">
                    PNG, JPG, GIF up to 5MB
                  </p>
                </div>
              </div>}
          </div>
          {/* Date */}
          <div data-id="element-162">
            <label htmlFor="date" className="block text-sm font-medium text-gray-700" data-id="element-163">
              <CalendarIcon className="inline-block w-4 h-4 mr-1" data-id="element-164" /> Date*
            </label>
            <input type="date" id="date" name="date" required value={eventData.date} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" data-id="element-165" />
          </div>
          {/* Time - Start and End */}
          <div className="flex space-x-4" data-id="element-166">
            <div className="flex-1" data-id="element-167">
              <label htmlFor="time" className="block text-sm font-medium text-gray-700" data-id="element-168">
                <ClockIcon className="inline-block w-4 h-4 mr-1" data-id="element-169" /> Start Time
              </label>
              <input type="time" id="time" name="time" value={eventData.time} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" data-id="element-170" />
            </div>
            <div className="flex-1" data-id="element-171">
              <label htmlFor="endTime" className="block text-sm font-medium text-gray-700" data-id="element-172">
                <ClockIcon className="inline-block w-4 h-4 mr-1" data-id="element-173" /> End Time
              </label>
              <input type="time" id="endTime" name="endTime" value={eventData.endTime} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" data-id="element-174" />
            </div>
          </div>
          {/* URL/Link Field with Extract Button */}
          <div data-id="element-175">
            <label htmlFor="link" className="block text-sm font-medium text-gray-700" data-id="element-176">
              <LinkIcon className="inline-block w-4 h-4 mr-1" data-id="element-177" /> URL/Link
            </label>
            <div className="mt-1 flex gap-2" data-id="element-178">
              <input type="url" id="link" name="link" value={eventData.link || ''} onChange={handleChange} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" placeholder="Paste Instagram, Facebook, or event link" data-id="element-179" />
              <button type="button" onClick={extractMetadataFromUrl} disabled={isExtractingUrl || !eventData.link} className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap" data-id="element-180">
                {isExtractingUrl ? <>
                    <LoaderIcon className="w-4 h-4 mr-1 animate-spin" data-id="element-181" />
                    Extracting...
                  </> : <>
                    <SparklesIcon className="w-4 h-4 mr-1" data-id="element-182" />
                    Extract Info
                  </>}
              </button>
            </div>
            {extractionError && <p className="mt-2 text-sm text-amber-600 flex items-start" data-id="element-183">
                <AlertCircleIcon className="w-4 h-4 mr-1 mt-0.5 flex-shrink-0" data-id="element-184" />
                <span data-id="element-185">{extractionError}</span>
              </p>}
            {!extractionError && <p className="mt-1 text-xs text-gray-500" data-id="element-186">
                Paste a link and click "Extract Info" to auto-fill event details
              </p>}
          </div>
          {/* Fee Information */}
          <div data-id="element-187">
            <label className="block text-sm font-medium text-gray-700" data-id="element-188">
              <DollarSignIcon className="inline-block w-4 h-4 mr-1" data-id="element-189" /> Admission
            </label>
            <div className="mt-2 flex space-x-4" data-id="element-190">
              <label className="inline-flex items-center" data-id="element-191">
                <input type="radio" checked={eventData.isFree} onChange={() => handleFeeOptionChange(true)} className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300" data-id="element-192" />
                <span className="ml-2 text-sm text-gray-700" data-id="element-193">Free</span>
              </label>
              <label className="inline-flex items-center" data-id="element-194">
                <input type="radio" checked={!eventData.isFree} onChange={() => handleFeeOptionChange(false)} className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300" data-id="element-195" />
                <span className="ml-2 text-sm text-gray-700" data-id="element-196">Paid</span>
              </label>
            </div>
            {!eventData.isFree && <div className="mt-2" data-id="element-197">
                <label htmlFor="feeAmount" className="block text-sm font-medium text-gray-700" data-id="element-198">
                  Fee Amount
                </label>
                <input type="text" id="feeAmount" name="feeAmount" value={eventData.feeAmount} onChange={handleChange} placeholder="$10, $5-20, or 'Donation-based'" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" data-id="element-199" />
              </div>}
          </div>
          {/* Registration Requirements */}
          <div data-id="element-200">
            <div className="flex items-center mb-2" data-id="element-201">
              <input type="checkbox" id="requiresRegistration" name="requiresRegistration" checked={eventData.requiresRegistration} onChange={handleChange} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-202" />
              <label htmlFor="requiresRegistration" className="ml-2 block text-sm font-medium text-gray-700" data-id="element-203">
                <ClipboardCheckIcon className="inline-block w-4 h-4 mr-1" data-id="element-204" />
                Requires registration
              </label>
            </div>
            {eventData.requiresRegistration && <div className="mt-2 pl-6" data-id="element-205">
                <label htmlFor="registrationDeadline" className="block text-sm font-medium text-gray-700" data-id="element-206">
                  Registration deadline
                </label>
                <input type="date" id="registrationDeadline" name="registrationDeadline" value={eventData.registrationDeadline} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" data-id="element-207" />
                <p className="mt-1 text-xs text-gray-500" data-id="element-208">
                  Optional: Date by which you need to register
                </p>
              </div>}
          </div>
          {/* Location */}
          <div data-id="element-209">
            <label htmlFor="location" className="block text-sm font-medium text-gray-700" data-id="element-210">
              <MapPinIcon className="inline-block w-4 h-4 mr-1" data-id="element-211" /> Location
            </label>
            <input type="text" id="location" name="location" value={eventData.location} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" placeholder="Venue, Address, City..." data-id="element-212" />
          </div>
          {/* Category */}
          <div data-id="element-213">
            <label htmlFor="category" className="block text-sm font-medium text-gray-700" data-id="element-214">
              <TagIcon className="inline-block w-4 h-4 mr-1" data-id="element-215" /> Category
            </label>
            <select id="category" name="category" value={eventData.category} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" data-id="element-216">
              <option value="" data-id="element-217">Select a category</option>
              <option value="music" data-id="element-218">Music</option>
              <option value="art" data-id="element-219">Art</option>
              <option value="food" data-id="element-220">Food & Drink</option>
              <option value="sports" data-id="element-221">Sports</option>
              <option value="outdoors" data-id="element-222">Outdoors</option>
              <option value="tech" data-id="element-223">Tech</option>
              <option value="community" data-id="element-224">Community</option>
              <option value="entertainment" data-id="element-225">Entertainment</option>
              <option value="other" data-id="element-226">Other</option>
            </select>
          </div>
          {/* Notes */}
          <div data-id="element-227">
            <label htmlFor="notes" className="block text-sm font-medium text-gray-700" data-id="element-228">
              Notes
            </label>
            <textarea id="notes" name="notes" rows={2} value={eventData.notes} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" placeholder="Any additional details..." data-id="element-229" />
          </div>
          {/* Status */}
          <div data-id="element-230">
            <label className="block text-sm font-medium text-gray-700" data-id="element-231">
              Status
            </label>
            <div className="mt-2 flex space-x-2" data-id="element-232">
              <label className="inline-flex items-center" data-id="element-233">
                <input type="radio" name="status" value="interested" checked={eventData.status === 'interested'} onChange={handleChange} className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300" data-id="element-234" />
                <span className="ml-2 text-sm text-gray-700" data-id="element-235">Interested</span>
              </label>
              <label className="inline-flex items-center" data-id="element-236">
                <input type="radio" name="status" value="going" checked={eventData.status === 'going'} onChange={handleChange} className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300" data-id="element-237" />
                <span className="ml-2 text-sm text-gray-700" data-id="element-238">Going</span>
              </label>
              <label className="inline-flex items-center" data-id="element-239">
                <input type="radio" name="status" value="waitlisted" checked={eventData.status === 'waitlisted'} onChange={handleChange} className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300" data-id="element-240" />
                <span className="ml-2 text-sm text-gray-700" data-id="element-241">Waitlisted</span>
              </label>
            </div>
          </div>
          {/* Submit Button */}
          <div className="pt-2" data-id="element-242">
            <button type="submit" className="w-full flex justify-center items-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" data-id="element-243">
              <SaveIcon className="w-4 h-4 mr-2" data-id="element-244" />
              {isEditing ? 'Update Event' : 'Save Event'}
            </button>
          </div>
        </div>
      </form>
    </div>;
}